/**
 * Form validation logic hook
 */

import { InfraDependenciesData } from '../types/formTypes';
import { isDBRequiredForTechStack } from '../utils/formHelpers';

interface FormValidationProps {
    techStack: string;
    serviceName: string;
    orgName: string;
    domain: string;
    description: string;
    maintainerTeams: string;
    infraDependenciesData: InfraDependenciesData;
    isAdvancedUser: boolean;
    isTechStackValid: boolean;
    isServiceNameValid: boolean;
    isOrgNameValid: boolean;
    isDomainValid: boolean;
    isFunctionalAreaValid: boolean;
    isMaintainerTeamsValid: boolean;
    isInfraDependenciesValid: boolean;
    isAdvancedOptionsValid: boolean;
    isDescriptionValid: boolean;
    generatedBy: string;
}

export const useFormValidation = (props: FormValidationProps) => {
    const {
        techStack,
        serviceName,
        orgName,
        description,
        domain,
        maintainerTeams,
        infraDependenciesData,
        isAdvancedUser,
        isTechStackValid,
        isServiceNameValid,
        isOrgNameValid,
        isDomainValid,
        isFunctionalAreaValid,
        isMaintainerTeamsValid,
        isInfraDependenciesValid,
        isAdvancedOptionsValid,
        isDescriptionValid,
        generatedBy,
    } = props;

    const isFormValid = (): boolean => {
        // Always validate for 'new' service type now
        const hasAllRequiredFields = techStack && serviceName && orgName && domain && generatedBy && maintainerTeams.trim().length > 0 && description.trim().length > 0;
        const hasDatabaseIfRequired = !isDBRequiredForTechStack(techStack) || infraDependenciesData.database;
        const hasNoValidationErrors = isTechStackValid &&
            isServiceNameValid && isOrgNameValid && isDomainValid && isFunctionalAreaValid &&
            isMaintainerTeamsValid && (!isDBRequiredForTechStack(techStack) || isInfraDependenciesValid) && isDescriptionValid;
        const hasAdvancedOptionsValid = !isAdvancedUser || isAdvancedOptionsValid;

        return !!(hasAllRequiredFields && hasDatabaseIfRequired && hasNoValidationErrors && hasAdvancedOptionsValid);
    };

    const validateAllFields = (
        validateTechStack: () => string,
        validateServiceName: () => string,
        validateOrgName: () => string,
        validateDomain: () => string,
        validateDescription: () => string,
        validateFunctionalArea: () => string,
        validateMaintainerTeams: () => void
    ) => {
        // Always validate for 'new' service type
        validateTechStack();
        validateServiceName();
        validateOrgName();
        validateDescription();
        validateDomain();
        validateFunctionalArea();
        validateMaintainerTeams();
    };

    return { 
        isFormValid,
        validateAllFields
    };
};
